import axios from 'axios';
import axiosAuthInstance from './axios-auth';
import clientEnv from '../env';
import { validateQuizGenerationRequest, normalizeQuestionType } from './quiz-validation';
import { buildContextualPrompt as buildContextualPromptFromModule, } from './quiz/quiz-prompts';
import { getQuestionTypeDistribution, selectRandomQuestionType } from './quiz/quiz-distribution';
import { mapQuestionType, mapDifficulty, mapDifficultyToLevel } from './quiz/quiz-response-mapper';
// Unified API configuration - prioritize backend server for consistency
// Note: API_BASE_URL is not used - this service uses axiosAuthInstance which has baseURL configured
// If you need to construct URLs, use getApiUrl() from api-url-helper.ts
const CLOUDFLARE_WORKER_URL = clientEnv.REACT_APP_QUIZ_WORKER_URL;
// Service priority: Backend first, then Cloudflare worker as fallback
const USE_BACKEND_FIRST = true;
// Enhanced debugging for quiz service (without exposing sensitive values)
// Validate worker URL
if (!CLOUDFLARE_WORKER_URL) {
    console.warn('Quiz worker URL not configured in environment. Backend fallback will be used.');
}
// Health check cache to avoid repeated calls
let healthCheckCache = null;
const HEALTH_CHECK_CACHE_DURATION = 30000; // 30 seconds
// Internal helper: fallback to main server API
function headerAiInteractionId(headers) {
    const raw = headers?.['x-ai-interaction-id'] ??
        headers?.['X-AI-Interaction-Id'];
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}
/**
 * Pull AI interaction id from quiz generation responses (single/batch/enhanced).
 */
export function extractQuizAiInteractionId(payload) {
    if (!payload || typeof payload !== 'object')
        return null;
    const p = payload;
    const top = p.aiInteractionId;
    if (typeof top === 'string' && top.trim())
        return top.trim();
    const data = p.data;
    const dm = data?.metadata;
    if (typeof dm?.aiInteractionId === 'string' && dm.aiInteractionId.trim()) {
        return dm.aiInteractionId.trim();
    }
    const rm = p.metadata;
    if (typeof rm?.aiInteractionId === 'string' && rm.aiInteractionId.trim()) {
        return rm.aiInteractionId.trim();
    }
    return null;
}
async function postToServerGenerate(requestPayload) {
    // Workers AI retries can take 60-90s; allow enough time for backend→worker round-trip
    const response = await axiosAuthInstance.post('/quiz/generate', requestPayload, {
        timeout: 120000,
    });
    const fromHeader = headerAiInteractionId(response.headers);
    const fromBody = extractQuizAiInteractionId(response.data);
    const id = fromHeader || fromBody;
    const body = response.data;
    if (id &&
        body?.data &&
        typeof body.data === 'object' &&
        body.data.metadata &&
        typeof body.data.metadata === 'object' &&
        body.data.metadata.aiInteractionId == null) {
        body.data.metadata.aiInteractionId = id;
    }
    return body;
}
export { ENHANCED_PROMPT_TEMPLATES } from './quiz/quiz-prompts';
export { getQuestionTypeDistribution, selectRandomQuestionType } from './quiz/quiz-distribution';
// Enhanced function to generate variation instructions for AI prompts
export function generateVariationInstructions(variationParams) {
    const instructions = [];
    if (variationParams.topicFocus) {
        const focusMap = {
            'conceptual': 'Focus on fundamental concepts and definitions',
            'practical': 'Emphasize real-world applications and examples',
            'theoretical': 'Concentrate on theoretical frameworks and principles',
            'historical': 'Include historical context and development',
            'current': 'Focus on contemporary issues and recent developments'
        };
        instructions.push(focusMap[variationParams.topicFocus]);
    }
    if (variationParams.questionStyle) {
        const styleMap = {
            'analytical': 'Require analytical thinking and problem-solving',
            'creative': 'Encourage creative and innovative thinking',
            'critical': 'Demand critical evaluation and assessment',
            'practical': 'Focus on practical application and implementation',
            'theoretical': 'Emphasize theoretical understanding and concepts'
        };
        instructions.push(styleMap[variationParams.questionStyle]);
    }
    if (variationParams.contextType) {
        const contextMap = {
            'real-world': 'Use real-world scenarios and practical examples',
            'academic': 'Focus on academic and theoretical contexts',
            'hypothetical': 'Present hypothetical situations for analysis',
            'case-study': 'Use case study approach with detailed scenarios'
        };
        instructions.push(contextMap[variationParams.contextType]);
    }
    if (variationParams.vocabularyLevel) {
        const vocabMap = {
            'basic': 'Use basic vocabulary suitable for beginners',
            'intermediate': 'Use intermediate vocabulary with some technical terms',
            'advanced': 'Use advanced vocabulary and technical terminology',
            'technical': 'Use specialized technical vocabulary and jargon'
        };
        instructions.push(vocabMap[variationParams.vocabularyLevel]);
    }
    if (variationParams.difficultyVariation) {
        const variationMap = {
            'consistent': 'Maintain consistent difficulty level throughout',
            'progressive': 'Gradually increase difficulty from easy to hard',
            'mixed': 'Mix different difficulty levels for variety'
        };
        instructions.push(variationMap[variationParams.difficultyVariation]);
    }
    return instructions.join('. ') + '.';
}
export class QuizAIService {
    static getInstance() {
        if (!QuizAIService.instance) {
            QuizAIService.instance = new QuizAIService();
        }
        return QuizAIService.instance;
    }
    /**
     * Validate question type compliance
     */
    validateQuestionTypeCompliance(question, expectedType) {
        switch (expectedType) {
            case 'true-false':
            case 'true_false':
                // Accept: correctAnswer in [true,false] AND (prefix "True or False:" OR statement format without ?)
                if (!question.correctAnswer || !['true', 'false'].includes(String(question.correctAnswer).toLowerCase())) {
                    return false;
                }
                const qText = question.question?.trim() || '';
                const hasPrefix = qText.startsWith('True or False:');
                const isStatement = !qText.includes('?') && qText.length >= 10;
                return hasPrefix || isStatement;
            case 'multiple-choice':
            case 'multiple_choice':
                return question.answerA && question.answerB && question.answerC &&
                    question.correctAnswer && ['A', 'B', 'C', 'D'].includes(question.correctAnswer);
            case 'short-answer':
            case 'short_answer':
                return question.question?.includes('?') && question.correctAnswer;
            case 'open-ended':
            case 'open_ended': {
                const ref = [question.correctAnswer, question.guidance, question.model_answer]
                    .find((x) => x != null && String(x).trim() !== '');
                return !!(question.question?.includes('?') && ref);
            }
            case 'scenario-based':
            case 'scenario_based': {
                const ref = [question.correctAnswer, question.guidance, question.model_answer]
                    .find((x) => x != null && String(x).trim() !== '');
                return !!(question.scenario && ref);
            }
            default:
                return true;
        }
    }
    /**
     * Build contextual prompt with enhanced context integration
     */
    buildContextualPrompt(params) {
        return buildContextualPromptFromModule(params);
    }
    /**
     * Create diversified prompt with enhanced anti-similarity instructions
     */
    createDiversifiedPrompt(basePrompt, questionIndex, questionType, existingQuestions, diversityParams) {
        const variationStrategies = [
            `Focus on ${diversityParams.contentVariation.focusArea[questionIndex % diversityParams.contentVariation.focusArea.length]}`,
            `Use ${diversityParams.semanticVariation.perspective} perspective`,
            `Apply ${diversityParams.contentVariation.contextType} context`,
            `Vary vocabulary to ${diversityParams.semanticVariation.vocabularyLevel} level`,
            `Use different sentence structure: ${diversityParams.semanticVariation.sentenceStructure}`
        ];
        const antiSimilarityInstructions = existingQuestions.length > 0 ? `
      CRITICAL: Avoid similarity with these existing questions:
      ${existingQuestions.map((q, i) => `${i + 1}. ${q.substring(0, 100)}...`).join('\n')}
      
      Ensure your question:
      - Uses completely different vocabulary
      - Focuses on different aspects of the topic
      - Has different sentence structure
      - Tests different learning objectives
      - Uses different examples or scenarios
    ` : '';
        return `${basePrompt}
    
    DIVERSITY REQUIREMENTS FOR QUESTION ${questionIndex + 1}:
    ${variationStrategies[questionIndex % variationStrategies.length]}
    
    ${antiSimilarityInstructions}
    
    Generate a UNIQUE question that meets these diversity requirements.`;
    }
    /**
     * Analyze content for better question generation
     */
    analyzeContentForQuestionGeneration(content, subtopics) {
        return {
            keyConcepts: this.extractKeyConcepts(content, subtopics),
            difficultyIndicators: this.analyzeDifficultyLevel(content),
            questionOpportunities: this.identifyQuestionOpportunities(content, subtopics),
            contextClues: this.extractContextClues(content)
        };
    }
    /**
     * Extract key concepts that align with subtopics
     */
    extractKeyConcepts(content, subtopics) {
        const words = content.toLowerCase().split(/\s+/);
        const conceptMap = new Map();
        // Count word frequency
        words.forEach(word => {
            if (word.length > 4) { // Only consider words longer than 4 characters
                conceptMap.set(word, (conceptMap.get(word) || 0) + 1);
            }
        });
        // Filter concepts that relate to subtopics
        const relevantConcepts = Array.from(conceptMap.entries())
            .filter(([concept, count]) => count > 2 && subtopics.some(subtopic => subtopic.toLowerCase().includes(concept) || concept.includes(subtopic.toLowerCase())))
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([concept]) => concept);
        return relevantConcepts;
    }
    /**
     * Analyze difficulty level indicators in content
     */
    analyzeDifficultyLevel(content) {
        const advancedTerms = ['analysis', 'synthesis', 'evaluation', 'complex', 'advanced', 'sophisticated'];
        const intermediateTerms = ['application', 'understanding', 'explanation', 'compare', 'contrast'];
        const basicTerms = ['define', 'identify', 'list', 'name', 'basic', 'simple'];
        const contentLower = content.toLowerCase();
        const advancedCount = advancedTerms.filter(term => contentLower.includes(term)).length;
        const intermediateCount = intermediateTerms.filter(term => contentLower.includes(term)).length;
        const basicCount = basicTerms.filter(term => contentLower.includes(term)).length;
        if (advancedCount > intermediateCount && advancedCount > basicCount)
            return 'hard';
        if (intermediateCount > basicCount)
            return 'medium';
        return 'easy';
    }
    /**
     * Identify question opportunities in content
     */
    identifyQuestionOpportunities(content, subtopics) {
        const opportunities = [];
        const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 20);
        sentences.forEach(sentence => {
            if (subtopics.some(subtopic => sentence.toLowerCase().includes(subtopic.toLowerCase()))) {
                opportunities.push(sentence.trim());
            }
        });
        return opportunities.slice(0, 5); // Return top 5 opportunities
    }
    /**
     * Extract context clues from content
     */
    extractContextClues(content) {
        const contextPatterns = [
            /for example/gi,
            /such as/gi,
            /including/gi,
            /specifically/gi,
            /in particular/gi
        ];
        const clues = [];
        contextPatterns.forEach(pattern => {
            const matches = content.match(pattern);
            if (matches) {
                clues.push(...matches);
            }
        });
        return [...new Set(clues)]; // Remove duplicates
    }
    /**
     * Generate a single quiz question using the new API structure
     */
    async generateQuestion(params) {
        try {
            // Validate request using centralized validation
            const validationResult = validateQuizGenerationRequest({
                region: params.region,
                level: params.level,
                subject: params.subject,
                subcategory: params.subcategory,
                questionType: params.questionType || 'multiple_choice',
                fileContent: params.fileContent
            });
            if (!validationResult.isValid) {
                const errorMessages = validationResult.errors.map(e => `${e.field}: ${e.message}`);
                throw new Error(`Validation failed: ${errorMessages.join(', ')}`);
            }
            // If autoDistributeQuestionTypes is enabled and no questionType is specified,
            // automatically select a question type based on difficulty
            if (params.options?.autoDistributeQuestionTypes && !params.questionType) {
                const difficulty = this.mapDifficultyToDistribution(params.level);
                params.questionType = normalizeQuestionType(selectRandomQuestionType(difficulty));
            }
            // Ensure questionType is provided (either from params or auto-selected)
            if (!params.questionType) {
                const difficulty = this.mapDifficultyToDistribution(params.level);
                params.questionType = normalizeQuestionType(selectRandomQuestionType(difficulty));
            }
            // Prepare the request payload with all required fields
            const requestPayload = {
                region: params.region,
                level: params.level,
                subject: params.subject,
                subcategory: params.subcategory,
                questionType: params.questionType,
                fileContent: params.fileContent,
                options: params.options
            };
            if (params.prompt != null && params.prompt !== '')
                requestPayload.prompt = params.prompt;
            if (params.lessonContent != null && params.lessonContent !== '') {
                requestPayload.lessonContent = params.lessonContent;
            }
            if (params.classroomId)
                requestPayload.classroomId = params.classroomId;
            if (params.difficulty != null && params.difficulty !== '')
                requestPayload.difficulty = params.difficulty;
            if (params.rubric_id)
                requestPayload.rubric_id = params.rubric_id;
            if (params.rubric && typeof params.rubric === 'object')
                requestPayload.rubric = params.rubric;
            if (params.style_rubric_id)
                requestPayload.style_rubric_id = params.style_rubric_id;
            if (params.qualityThreshold != null)
                requestPayload.qualityThreshold = params.qualityThreshold;
            if (params.validationLevel)
                requestPayload.validationLevel = params.validationLevel;
            if (params.includeExplanation != null) {
                requestPayload.includeExplanation = params.includeExplanation;
            }
            if (params.includeReferences != null) {
                requestPayload.includeReferences = params.includeReferences;
            }
            if (params.targeted_skill != null && String(params.targeted_skill).trim() !== '') {
                requestPayload.targeted_skill = String(params.targeted_skill).trim().slice(0, 256);
            }
            // Use smart fallback strategy: try backend only if authenticated, otherwise use worker
            if (USE_BACKEND_FIRST) {
                try {
                    return await postToServerGenerate(requestPayload);
                }
                catch (serverErr) {
                    const errorCode = serverErr.response?.status;
                    console.warn(`Backend server failed (${errorCode}), falling back to worker:`, serverErr.message);
                    // If backend fails due to auth issues (401, 403) or not found (404), try worker directly
                    if (errorCode === 401 || errorCode === 403 || errorCode === 404) {
                    }
                    // Fallback to Cloudflare Worker if available. Board-style requests
                    // (style_rubric_id) must be composed server-side, so they never fall
                    // back to the worker directly.
                    if (CLOUDFLARE_WORKER_URL && !params.style_rubric_id) {
                        try {
                            const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, requestPayload, {
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                timeout: 30000,
                            });
                            if (response.data.success) {
                                return response.data;
                            }
                            else {
                                console.error('Worker returned error:', response.data.error);
                                throw new Error(response.data.error?.message || 'Worker generation failed');
                            }
                        }
                        catch (workerErr) {
                            console.error('Worker also failed:', workerErr.message);
                            // If worker fails due to API key issues, provide helpful error
                            if (workerErr.message?.includes('credentials') || workerErr.message?.includes('401')) {
                                throw new Error('Quiz generation is temporarily unavailable. Please try again later or contact support if the issue persists.');
                            }
                            throw new Error(`Generation failed: Backend: ${serverErr.message}, Worker: ${workerErr.message}`);
                        }
                    }
                    else if (params.style_rubric_id) {
                        // Prefer the API's concrete error (worker URL missing, Worker error NNN, etc.)
                        // over axios's generic "Request failed with status code 500".
                        const apiDetail = serverErr.response?.data?.error?.details ||
                            serverErr.response?.data?.error?.message ||
                            serverErr.response?.data?.message ||
                            serverErr.message;
                        throw new Error(`Board-style generation failed (server-side composition required): ${apiDetail}`);
                    }
                    else {
                        throw new Error(`Backend generation failed and no worker configured: ${serverErr.message}`);
                    }
                }
            }
            else {
                // Legacy worker-first approach (kept for backward compatibility)
                try {
                    const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, requestPayload, {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        timeout: 30000,
                    });
                    return response.data;
                }
                catch (err) {
                    console.warn('Worker call failed, attempting server fallback');
                    try {
                        return await postToServerGenerate(requestPayload);
                    }
                    catch (serverErr) {
                        throw serverErr;
                    }
                }
            }
        }
        catch (error) {
            console.error('Error generating question:', error);
            throw this.handleWorkerError(error);
        }
    }
    /**
     * Generate multiple questions with automatic question type distribution
     */
    async generateQuestionsWithAutoDistribution(baseParams, questionCount) {
        const questions = [];
        const difficulty = this.mapDifficultyToDistribution(baseParams.level);
        for (let i = 0; i < questionCount; i++) {
            const questionType = selectRandomQuestionType(difficulty);
            const params = {
                ...baseParams,
                questionType: questionType,
                options: {
                    ...baseParams.options,
                    autoDistributeQuestionTypes: true,
                },
            };
            const question = await this.generateQuestion(params);
            questions.push(question);
        }
        return questions;
    }
    /**
     * Map level to difficulty for question type distribution
     */
    mapDifficultyToDistribution(level) {
        return mapDifficulty(level);
    }
    /**
     * Select a legacy question type based on difficulty for backward compatibility
     */
    selectLegacyQuestionType(difficulty) {
        const distribution = getQuestionTypeDistribution(difficulty);
        const random = Math.random();
        let cumulative = 0;
        // Map new question types to legacy types
        const legacyTypeMap = {
            'multiple-choice': 'multiple_choice',
            'true-false': 'true_false',
            'short-answer': 'short_answer',
            'open-ended': 'open_ended',
            'scenario-based': 'scenario_based',
        };
        for (const [questionType, probability] of Object.entries(distribution)) {
            cumulative += probability;
            if (random <= cumulative) {
                return legacyTypeMap[questionType] || 'multiple_choice';
            }
        }
        return 'multiple_choice';
    }
    /**
     * Get available options for quiz generation
     */
    async getOptions() {
        try {
            const response = await axios.get(`${CLOUDFLARE_WORKER_URL}/options`, {
                timeout: 8000, // Reduced from 10s to 8s
                headers: {
                    'Accept': 'application/json'
                    // Note: User-Agent header removed - browsers block it for security
                }
            });
            return response.data;
        }
        catch (error) {
            console.error('Error fetching options:', error);
            throw this.handleWorkerError(error);
        }
    }
    /**
     * Generate multiple questions in a single request for efficiency.
     * Tries: backend batch → worker batch → sequential single-question fallback.
     */
    async generateQuestionsBatch(params) {
        try {
            const { options, ...baseParams } = params;
            const batchSize = options.batchSize || 1;
            if (batchSize === 1) {
                return this.generateQuestion(baseParams);
            }
            // 1. Try backend batch endpoint (backend calls worker /generate/batch internally)
            if (USE_BACKEND_FIRST) {
                try {
                    const response = await axiosAuthInstance.post('/quiz/generate/batch', {
                        ...baseParams,
                        batchSize,
                        options: {
                            ...options,
                            generateMultiple: true,
                            ensureVariety: true
                        }
                    }, {
                        timeout: 120000,
                    });
                    const id = headerAiInteractionId(response.headers) ||
                        extractQuizAiInteractionId(response.data);
                    const body = response.data;
                    if (id && body && typeof body === 'object' && body.aiInteractionId == null) {
                        body.aiInteractionId = id;
                    }
                    return body;
                }
                catch (serverErr) {
                    console.warn(`Backend batch generation failed (${serverErr.response?.status}), trying worker batch:`, serverErr.message);
                }
            }
            // 2. Try worker batch endpoint directly (single AI call for all questions).
            // Board-style requests must go through the backend so the style rubric is
            // composed server-side — never call the worker directly for them.
            if (CLOUDFLARE_WORKER_URL && !baseParams.style_rubric_id) {
                try {
                    const workerResponse = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate/batch`, {
                        ...baseParams,
                        batchSize,
                        options,
                    }, {
                        headers: { 'Content-Type': 'application/json' },
                        timeout: 90000,
                    });
                    if (workerResponse.data?.success && Array.isArray(workerResponse.data.data)) {
                        return workerResponse.data;
                    }
                }
                catch (workerErr) {
                    console.warn('Worker batch endpoint failed, falling back to sequential:', workerErr.message);
                }
            }
            // 3. Last resort: sequential single-question generation
            return this.generateQuestionsSequentially(baseParams, batchSize);
        }
        catch (error) {
            console.error('Batch generation failed:', error);
            throw error;
        }
    }
    /**
     * Fallback sequential generation when batch is not available
     */
    async generateQuestionsSequentially(params, count) {
        const questions = [];
        const errors = [];
        // If autoDistributeQuestionTypes is enabled, generate questions with variety
        if (params.options?.autoDistributeQuestionTypes) {
            const difficulty = this.mapDifficultyToDistribution(params.level);
            // Define question type distribution based on difficulty
            const difficultyDistribution = {
                'easy': ['multiple_choice', 'true_false', 'multiple_choice', 'true_false', 'short_answer'],
                'medium': ['multiple_choice', 'short_answer', 'true_false', 'open_ended', 'scenario_based'],
                'hard': ['short_answer', 'open_ended', 'scenario_based', 'multiple_choice', 'open_ended']
            };
            const distribution = difficultyDistribution[difficulty] || difficultyDistribution['medium'];
            for (let i = 0; i < count; i++) {
                try {
                    // Select question type based on difficulty distribution
                    const questionType = distribution[i % distribution.length];
                    const response = await this.generateQuestion({
                        ...params,
                        questionType: questionType,
                        options: {
                            ...params.options,
                            additionalContext: `Generate question ${i + 1} of ${count} with different content and type: ${questionType}`,
                            skipCache: i > 0 // Skip cache for subsequent questions to ensure variety
                        }
                    });
                    if (response.success) {
                        questions.push(response.data);
                    }
                }
                catch (error) {
                    errors.push(error);
                    console.warn(`Failed to generate question ${i + 1}:`, error);
                }
                // Small delay between requests to prevent overwhelming
                if (i < count - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
        }
        else {
            // Original logic for when autoDistributeQuestionTypes is disabled
            for (let i = 0; i < count; i++) {
                try {
                    const response = await this.generateQuestion({
                        ...params,
                        options: {
                            ...params.options,
                            additionalContext: `Generate question ${i + 1} of ${count} with different content`,
                            skipCache: i > 0 // Skip cache for subsequent questions to ensure variety
                        }
                    });
                    if (response.success) {
                        questions.push(response.data);
                    }
                }
                catch (error) {
                    errors.push(error);
                    console.warn(`Failed to generate question ${i + 1}:`, error);
                }
                // Small delay between requests to prevent overwhelming
                if (i < count - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
        }
        if (questions.length === 0) {
            throw new Error(`Failed to generate any questions: ${errors.map((e) => e.message).join(', ')}`);
        }
        return {
            success: true,
            data: questions[0] // Return the first question for batch operations
        };
    }
    /**
     * Check worker health status with retry mechanism, memory optimization, and caching
     */
    async checkWorkerHealth() {
        // Check cache first to avoid repeated calls
        const now = Date.now();
        if (healthCheckCache && (now - healthCheckCache.timestamp) < HEALTH_CHECK_CACHE_DURATION) {
            return healthCheckCache.result;
        }
        const maxRetries = 2; // Reduced from 3 to save memory
        const retryDelay = 500; // Reduced from 1000ms to 500ms
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                // Use fetch instead of axios for better memory efficiency
                const controller = new AbortController();
                const timeoutId = setTimeout(() => {
                    controller.abort();
                }, 5000); // Increased to 5 seconds for better reliability
                const response = await fetch(`${CLOUDFLARE_WORKER_URL}/health`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Cache-Control': 'no-cache'
                    },
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
                const isHealthy = response.ok;
                if (isHealthy && response.headers.get('content-type')?.includes('application/json')) {
                    const data = await response.json();
                }
                // Cache the result
                healthCheckCache = { result: isHealthy, timestamp: now };
                return isHealthy;
            }
            catch (error) {
                const isLastAttempt = attempt === maxRetries;
                const isAborted = error.name === 'AbortError';
                const isTimeout = error.code === 'ECONNABORTED' || isAborted;
                console.error(`🏥 Worker health check failed (attempt ${attempt}/${maxRetries}):`, {
                    message: error.message,
                    name: error.name,
                    code: error.code,
                    timeout: isTimeout,
                    url: `${CLOUDFLARE_WORKER_URL}/health`,
                    willRetry: !isLastAttempt,
                    stack: error.stack
                });
                if (isLastAttempt) {
                    // Cache the failure result to avoid repeated attempts
                    healthCheckCache = { result: false, timestamp: now };
                    return false;
                }
                // Wait before retrying (shorter delay to save memory)
                if (!isLastAttempt) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
            }
        }
        // Cache the failure result
        healthCheckCache = { result: false, timestamp: now };
        return false;
    }
    /**
     * Check whether a figure spec is drawable.
     *
     * Returns the parsed spec, never an image URL: quiz visuals are typed specs
     * that `src/figures` draws in the browser. There is no image-generation model
     * behind this call.
     */
    async validateFigureSpec(figure) {
        try {
            const response = await axiosAuthInstance.post('/quiz/generate/visual', { figure }, { timeout: 15000 });
            return { success: true, figure: response.data?.data?.figure };
        }
        catch (error) {
            const message = error?.response?.data?.error
                ?.message || this.handleWorkerError(error).message;
            return { success: false, error: message };
        }
    }
    /**
     * Generate enhanced question with quality validation
     */
    async generateEnhancedQuestion(request) {
        try {
            // Ensure required fields are present
            const enhancedRequest = {
                ...request,
                region: request.region || 'UK',
                level: request.level || 'intermediate',
                subject: request.subject || 'Mathematics',
                subcategory: request.subcategory || 'General',
                qualityThreshold: request.qualityThreshold || 0.7,
                validationLevel: request.validationLevel || 'basic',
                includeExplanation: request.includeExplanation !== false,
                includeReferences: request.includeReferences || false
            };
            const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate/enhanced`, enhancedRequest, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 90000, // 90 seconds for enhanced generation with validation
            });
            // Handle enhanced response format directly
            if (response.data.success && response.data.questions) {
                return {
                    success: true,
                    questions: response.data.questions,
                    metadata: response.data.metadata
                };
            }
            else {
                throw new Error(response.data.error?.message || 'Failed to generate enhanced question');
            }
        }
        catch (error) {
            console.error('Error generating enhanced question:', error);
            throw this.handleWorkerError(error);
        }
    }
    /**
     * Get analytics and cache statistics
     */
    async getAnalytics() {
        try {
            const response = await axios.get(`${CLOUDFLARE_WORKER_URL}/analytics`, {
                timeout: 8000, // Reduced from 10s to 8s
                headers: {
                    'Accept': 'application/json'
                    // Note: User-Agent header removed - browsers block it for security
                }
            });
            return response.data;
        }
        catch (error) {
            console.error('Error fetching analytics:', error);
            throw this.handleWorkerError(error);
        }
    }
    // Legacy methods for backward compatibility
    async generateQuestionsFromFile(file, options) {
        try {
            // If autoDistributeQuestionTypes is enabled, generate questions with automatic distribution
            if (options.autoDistributeQuestionTypes) {
                return this.generateQuestionsFromFileWithAutoDistribution(file, options);
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('questionCount', options.questionCount.toString());
            formData.append('difficulty', options.difficulty);
            formData.append('questionType', options.questionType || 'multiple_choice');
            if (options.subject) {
                formData.append('subject', options.subject);
            }
            if (options.subtopics && options.subtopics.length > 0) {
                formData.append('subtopics', JSON.stringify(options.subtopics));
            }
            const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 60000, // 60 seconds for AI processing
            });
            return this.processWorkerResponse(response.data);
        }
        catch (error) {
            console.error('Error generating questions from file:', error);
            throw this.handleWorkerError(error);
        }
    }
    /**
     * Generate questions from file with automatic question type distribution
     */
    async generateQuestionsFromFileWithAutoDistribution(file, options) {
        const questions = [];
        const difficulty = options.difficulty;
        const usedQuestionTypes = new Set();
        for (let i = 0; i < options.questionCount; i++) {
            // Select a question type that hasn't been used yet, or random if all have been used
            let questionType = this.selectLegacyQuestionType(difficulty);
            if (usedQuestionTypes.size < 5) { // We have 5 question types max
                // Try to use a different question type
                const availableTypes = ['multiple_choice', 'true_false', 'short_answer', 'open_ended', 'scenario_based'].filter(type => !usedQuestionTypes.has(type));
                if (availableTypes.length > 0) {
                    questionType = availableTypes[Math.floor(Math.random() * availableTypes.length)];
                }
            }
            usedQuestionTypes.add(questionType);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('questionCount', '1');
            formData.append('difficulty', difficulty);
            formData.append('questionType', questionType);
            if (options.subject) {
                formData.append('subject', options.subject);
            }
            if (options.subtopics && options.subtopics.length > 0) {
                formData.append('subtopics', JSON.stringify(options.subtopics));
            }
            try {
                const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    timeout: 60000,
                });
                const result = this.processWorkerResponse(response.data);
                if (result.questions.length > 0) {
                    const question = result.questions[0];
                    // Check if this question is too similar to existing ones
                    const isDuplicate = questions.some(existingQuestion => this.calculateSimilarity(existingQuestion.question, question.question) > 0.8);
                    if (!isDuplicate) {
                        questions.push(question);
                    }
                    else {
                        // Retry this iteration with a different approach
                        i--; // Retry this iteration
                        continue;
                    }
                }
                else {
                }
            }
            catch (error) {
                console.error(`🏥 Error generating file question ${i + 1}:`, error);
                // Continue with next question instead of failing completely
            }
        }
        return {
            success: true,
            questions,
            metadata: {
                generatedAt: new Date().toISOString(),
                source: 'file',
                processingTime: 0,
            },
        };
    }
    async generateQuestionsFromPrompt(prompt, options) {
        try {
            // If autoDistributeQuestionTypes is enabled, generate questions with automatic distribution
            if (options.autoDistributeQuestionTypes) {
                return this.generateQuestionsFromPromptWithAutoDistribution(prompt, options);
            }
            // Use legacy format that the Cloudflare worker expects
            const payload = {
                prompt,
                questionCount: options.questionCount,
                difficulty: options.difficulty,
                questionType: options.questionType || 'multiple_choice',
                subject: options.subject || 'General',
                subtopics: options.subtopics || [],
            };
            const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 30000, // 30 seconds for prompt-based generation
            });
            return this.processWorkerResponse(response.data);
        }
        catch (error) {
            console.error('Error generating questions from prompt:', error);
            throw this.handleWorkerError(error);
        }
    }
    /**
     * Generate questions from prompt with automatic question type distribution
     */
    async generateQuestionsFromPromptWithAutoDistribution(prompt, options) {
        const questions = [];
        const difficulty = options.difficulty;
        const usedQuestionTypes = new Set();
        const usedPrompts = new Set();
        for (let i = 0; i < options.questionCount; i++) {
            // Select a question type that hasn't been used yet, or random if all have been used
            let questionType = this.selectLegacyQuestionType(difficulty);
            if (usedQuestionTypes.size < 5) { // We have 5 question types max
                // Try to use a different question type
                const availableTypes = ['multiple_choice', 'true_false', 'short_answer', 'open_ended', 'scenario_based'].filter(type => !usedQuestionTypes.has(type));
                if (availableTypes.length > 0) {
                    questionType = availableTypes[Math.floor(Math.random() * availableTypes.length)];
                }
            }
            usedQuestionTypes.add(questionType);
            // Create a varied prompt to ensure different questions
            let variedPrompt = this.createVariedPrompt(prompt, i, questionType, difficulty);
            // Ensure we don't use the same prompt twice
            if (usedPrompts.has(variedPrompt)) {
                // Add more variation if we've already used this prompt
                const extraVariation = ` (Question ${i + 1} with focus on ${this.getRandomFocusArea()})`;
                variedPrompt += extraVariation;
            }
            usedPrompts.add(variedPrompt);
            const payload = {
                prompt: variedPrompt,
                questionCount: 1,
                difficulty,
                questionType,
                subject: options.subject || 'General',
                subtopics: options.subtopics || [],
            };
            try {
                const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, payload, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    timeout: 30000,
                });
                const result = this.processWorkerResponse(response.data);
                if (result.questions.length > 0) {
                    const question = result.questions[0];
                    // Check if this question is too similar to existing ones
                    const isDuplicate = questions.some(existingQuestion => this.calculateSimilarity(existingQuestion.question, question.question) > 0.8);
                    if (!isDuplicate) {
                        questions.push(question);
                    }
                    else {
                        // Retry this iteration with a different approach
                        i--; // Retry this iteration
                        continue;
                    }
                }
                else {
                }
            }
            catch (error) {
                console.error(`🏥 Error generating question ${i + 1}:`, error);
                // Continue with next question instead of failing completely
            }
        }
        return {
            success: true,
            questions,
            metadata: {
                generatedAt: new Date().toISOString(),
                source: 'prompt',
                processingTime: 0,
            },
        };
    }
    /**
     * Create a varied prompt to ensure different questions (Enhanced version)
     */
    createVariedPrompt(basePrompt, questionIndex, questionType, difficulty) {
        // Create enhanced diversity parameters
        const diversityParams = {
            semanticVariation: {
                vocabularyLevel: difficulty === 'easy' ? 'basic' : difficulty === 'medium' ? 'intermediate' : 'advanced',
                sentenceStructure: 'varied',
                perspective: ['student', 'teacher', 'practitioner', 'researcher'][questionIndex % 4]
            },
            contentVariation: {
                focusArea: ['conceptual understanding', 'practical application', 'theoretical knowledge', 'problem-solving', 'critical thinking'],
                difficultyProgression: 'mixed',
                contextType: ['theoretical', 'practical', 'real-world', 'hypothetical'][questionIndex % 4]
            },
            structuralVariation: {
                questionStarters: ['What', 'How', 'Why', 'Which', 'When', 'Where', 'Explain', 'Compare', 'Analyze', 'Evaluate'],
                exampleTypes: ['real-world examples', 'hypothetical scenarios', 'case studies', 'practical applications'],
                scenarioVariations: ['academic context', 'professional setting', 'everyday situation', 'research scenario']
            }
        };
        // Use the enhanced diversified prompt system
        return this.createDiversifiedPrompt(basePrompt, questionIndex, questionType, [], // No existing questions for this method
        diversityParams);
    }
    /**
     * Get a random focus area for additional prompt variation
     */
    getRandomFocusArea() {
        const focusAreas = [
            'practical application',
            'theoretical understanding',
            'problem-solving approach',
            'conceptual knowledge',
            'analytical thinking',
            'real-world scenarios',
            'step-by-step process',
            'critical analysis',
            'creative thinking',
            'logical reasoning'
        ];
        return focusAreas[Math.floor(Math.random() * focusAreas.length)];
    }
    /**
     * Calculate similarity between two questions using simple text comparison
     */
    calculateSimilarity(question1, question2) {
        const words1 = question1.toLowerCase().split(/\s+/);
        const words2 = question2.toLowerCase().split(/\s+/);
        const commonWords = words1.filter(word => words2.includes(word));
        const totalWords = new Set([...words1, ...words2]).size;
        return commonWords.length / totalWords;
    }
    async generateQuestionsFromLesson(lessonContent, options) {
        try {
            // If autoDistributeQuestionTypes is enabled, generate questions with automatic distribution
            if (options.autoDistributeQuestionTypes) {
                return this.generateQuestionsFromLessonWithAutoDistribution(lessonContent, options);
            }
            const payload = {
                lessonContent,
                questionCount: options.questionCount,
                difficulty: options.difficulty,
                questionType: options.questionType || 'multiple_choice',
                subject: options.subject,
                subtopics: options.subtopics,
            };
            const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 30000, // 30 seconds for lesson-based generation
            });
            return this.processWorkerResponse(response.data);
        }
        catch (error) {
            console.error('Error generating questions from lesson:', error);
            throw this.handleWorkerError(error);
        }
    }
    /**
     * Generate questions from lesson with automatic question type distribution
     */
    async generateQuestionsFromLessonWithAutoDistribution(lessonContent, options) {
        const questions = [];
        const difficulty = options.difficulty;
        const usedQuestionTypes = new Set();
        const usedContentVariations = new Set();
        for (let i = 0; i < options.questionCount; i++) {
            // Select a question type that hasn't been used yet, or random if all have been used
            let questionType = this.selectLegacyQuestionType(difficulty);
            if (usedQuestionTypes.size < 5) { // We have 5 question types max
                // Try to use a different question type
                const availableTypes = ['multiple_choice', 'true_false', 'short_answer', 'open_ended', 'scenario_based'].filter(type => !usedQuestionTypes.has(type));
                if (availableTypes.length > 0) {
                    questionType = availableTypes[Math.floor(Math.random() * availableTypes.length)];
                }
            }
            usedQuestionTypes.add(questionType);
            // Create a varied lesson content to ensure different questions
            let variedContent = this.createVariedLessonContent(lessonContent, i, questionType, difficulty);
            // Ensure we don't use the same content variation twice
            if (usedContentVariations.has(variedContent)) {
                // Add more variation if we've already used this content
                const extraVariation = ` (Focus on ${this.getRandomFocusArea()} for question ${i + 1})`;
                variedContent += extraVariation;
            }
            usedContentVariations.add(variedContent);
            const payload = {
                lessonContent: variedContent,
                questionCount: 1,
                difficulty,
                questionType,
                subject: options.subject,
                subtopics: options.subtopics,
            };
            try {
                const response = await axios.post(`${CLOUDFLARE_WORKER_URL}/generate`, payload, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    timeout: 30000,
                });
                const result = this.processWorkerResponse(response.data);
                if (result.questions.length > 0) {
                    const question = result.questions[0];
                    // Check if this question is too similar to existing ones
                    const isDuplicate = questions.some(existingQuestion => this.calculateSimilarity(existingQuestion.question, question.question) > 0.8);
                    if (!isDuplicate) {
                        questions.push(question);
                    }
                    else {
                        // Retry this iteration with a different approach
                        i--; // Retry this iteration
                        continue;
                    }
                }
                else {
                }
            }
            catch (error) {
                console.error(`🏥 Error generating lesson question ${i + 1}:`, error);
                // Continue with next question instead of failing completely
            }
        }
        return {
            success: true,
            questions,
            metadata: {
                generatedAt: new Date().toISOString(),
                source: 'lesson',
                processingTime: 0,
            },
        };
    }
    /**
     * Create a varied lesson content to ensure different questions
     */
    createVariedLessonContent(baseContent, questionIndex, questionType, difficulty) {
        const variations = [
            `Based on this lesson content, create a ${difficulty} ${questionType} question: ${baseContent}`,
            `From the following lesson material, generate a ${questionType} question (${difficulty} level): ${baseContent}`,
            `Using this lesson content, develop a ${difficulty} ${questionType} question: ${baseContent}`,
            `Based on the lesson below, formulate a ${questionType} question (${difficulty} difficulty): ${baseContent}`,
            `From this lesson material, construct a ${difficulty} ${questionType} question: ${baseContent}`,
            `Using the lesson content, design a ${questionType} question (${difficulty} level): ${baseContent}`,
            `Based on the following lesson, build a ${difficulty} ${questionType} question: ${baseContent}`,
            `From this lesson, create an engaging ${questionType} question (${difficulty} level): ${baseContent}`,
            `Using the lesson material, generate a challenging ${questionType} question (${difficulty} difficulty): ${baseContent}`,
            `Based on the lesson content, develop a comprehensive ${questionType} question (${difficulty} level): ${baseContent}`
        ];
        // Use question index to select variation, cycling through them
        const variationIndex = questionIndex % variations.length;
        return variations[variationIndex];
    }
    processWorkerResponse(data) {
        if (!data.success) {
            throw new Error(data.error?.message || 'Unknown error occurred');
        }
        // Handle both new and legacy response formats
        let questions = [];
        let metadata = {
            generatedAt: new Date().toISOString(),
            source: 'prompt',
            processingTime: 0,
        };
        if (data.questions) {
            // Legacy format: data.questions array
            questions = data.questions.map((q) => ({
                id: q.id,
                type: this.mapQuestionType(q.type),
                question: q.question,
                difficulty: this.mapDifficulty(q.difficulty),
                answerA: q.answerA,
                answerB: q.answerB,
                answerC: q.answerC,
                answerD: q.answerD,
                correctAnswer: q.correctAnswer,
                scenario: q.scenario,
                sentence: q.sentence,
            }));
            metadata = {
                generatedAt: data.metadata?.generatedAt || new Date().toISOString(),
                source: data.metadata?.source || 'prompt',
                processingTime: data.metadata?.processingTime || 0,
            };
        }
        else if (data.data) {
            // New format: data.data object
            questions = [{
                    id: data.data.id,
                    type: this.mapQuestionType(data.data.question.type),
                    question: data.data.question.rawText,
                    difficulty: this.mapDifficulty(data.data.metadata.difficulty),
                    correctAnswer: data.data.question.formatted.answer,
                    ...(data.data.question.formatted.options && {
                        answerA: data.data.question.formatted.options[0],
                        answerB: data.data.question.formatted.options[1],
                        answerC: data.data.question.formatted.options[2],
                        answerD: data.data.question.formatted.options[3],
                    })
                }];
            metadata = {
                generatedAt: data.data?.metadata?.generatedAt || new Date().toISOString(),
                source: 'prompt',
                processingTime: 0,
            };
        }
        return {
            success: true,
            questions: questions,
            metadata: {
                generatedAt: new Date().toISOString(),
                source: 'prompt',
                processingTime: 0
            }
        };
    }
    mapQuestionType(type) {
        return mapQuestionType(type);
    }
    mapDifficulty(difficulty) {
        return mapDifficulty(difficulty);
    }
    mapDifficultyToLevel(difficulty) {
        return mapDifficultyToLevel(difficulty);
    }
    handleWorkerError(error) {
        console.error('🚨 Quiz Worker Error Details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
            timeout: error.config?.timeout
        });
        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const data = error.response.data;
            let message = 'Quiz generation failed';
            if (status === 400) {
                message = data?.error?.message || data?.message || 'Bad request - check your input parameters';
            }
            else if (status === 401) {
                message = 'Authentication required. Please log in to use the quiz generator.';
            }
            else if (status === 403) {
                message = 'Access denied. Please check your permissions or try again later.';
            }
            else if (status === 404) {
                message = 'Quiz service not found. Using fallback generation method...';
            }
            else if (status === 500) {
                message =
                    data?.error?.details ||
                        data?.error?.message ||
                        data?.message ||
                        'Quiz service temporarily unavailable. Please try again in a few moments.';
            }
            else {
                message = data?.error?.message || data?.message || `Service error (${status}): ${error.response.statusText}`;
            }
            return new Error(message);
        }
        else if (error.request) {
            // Request was made but no response received
            return new Error('Unable to connect to quiz service. Please check your internet connection and try again.');
        }
        else {
            // Something else happened
            return new Error(error.message || 'An unexpected error occurred during quiz generation');
        }
    }
}
// Export singleton instance
export const quizAIService = QuizAIService.getInstance();
